<script setup lang="ts">
import {
  computed,
  nextTick,
  onUnmounted,
  useId,
  useTemplateRef,
  watch,
} from 'vue';
import BaseIcon from '@/js/account/components/base/BaseIcon.vue';
import BaseTitle from '@/js/account/components/base/BaseTitle.vue';
import {
  closeAccountFancyboxDialog,
  openAccountFancyboxDialog,
} from '@/js/account/shared/lib/fancybox-dialog';
import { classNames } from '@/js/lib/bem';

const props = withDefaults(
  defineProps<{
    closeLocked?: boolean
    maxWidth?: number
    title?: string
  }>(),
  {
    closeLocked: false,
    maxWidth: 380,
    title: undefined,
  },
);

const emit = defineEmits<{
  close: []
}>();

const generatedTitleId = useId();

const resolvedTitleId = computed(() => generatedTitleId);

const open = defineModel<boolean>('open', { default: false });

const cn = classNames('base-modal');

const panelRef = useTemplateRef<HTMLElement>('panel');
let skipCloseOnDestroy = false;

function resetPanelVisibility(): void {
  panelRef.value?.style.removeProperty('display');
}

async function showDialog(): Promise<void> {
  await nextTick();

  const panel = panelRef.value;

  if (!panel) {
    open.value = false;
    return;
  }

  try {
    await openAccountFancyboxDialog(panel, {
      on: {
        shouldClose: (_fancybox, event) => {
          if (props.closeLocked) {
            event?.preventDefault();
          }
        },
        destroy: () => {
          resetPanelVisibility();

          if (!skipCloseOnDestroy && open.value) {
            open.value = false;
            emit('close');
          }

          skipCloseOnDestroy = false;
        },
      },
    });
  }
  catch {
    open.value = false;
  }
}

function hideDialog(): void {
  skipCloseOnDestroy = true;
  void closeAccountFancyboxDialog();
}

function onCloseClick(): void {
  if (props.closeLocked) {
    return;
  }

  skipCloseOnDestroy = true;
  open.value = false;
  emit('close');
}

watch(open, (isOpen) => {
  if (isOpen) {
    skipCloseOnDestroy = false;
    void showDialog();
    return;
  }

  hideDialog();
});

onUnmounted(() => {
  hideDialog();
});
</script>

<template>
  <Teleport to="#account-fancybox-root">
    <div
      ref="panel"
      :class="cn()"
      data-selectable
      :style="{ maxWidth: `${maxWidth}px` }"
    >
      <div :class="cn('inner')">
        <button
          type="button"
          :class="cn('close')"
          aria-label="Close"
          :disabled="closeLocked"
          @click="onCloseClick"
        >
          <BaseIcon name="x" :width="24" :height="24" />
        </button>

        <BaseTitle
          v-if="title"
          :id="resolvedTitleId"
          size="md"
          :class="cn('title')"
        >
          {{ title }}
        </BaseTitle>

        <div
          :class="cn('content')"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="title ? resolvedTitleId : undefined"
        >
          <slot />
        </div>
      </div>
    </div>
  </Teleport>
</template>
